import {
  // eslint-disable-next-line no-unused-vars
  h,
  Component,
  render,
  createRef,
} from 'preact';
import axios, { CancelToken } from 'axios';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

import ResultList from './search/ResultList';

gsap.registerPlugin(ScrollToPlugin);

class SearchModule extends Component {
  state = {
    query: '',
    filter: [],
    page: 1,
    maxPages: 1,
    entries: [],
    count: [],
    loading: false,
    filterLoading: false,
    paginating: false,
  };

  constructor(props) {
    super(props);

    this.cancelToken = () => {};
    this.inputRef = createRef();

    document.documentElement.addEventListener('keydown', (event) => {
      const { keyCode } = event;

      if (keyCode === 27) {
        this.closeModal();
      }
    });
  }

  handleInputChange = (event) => {
    this.setState(
      {
        page: 1,
        query: event.target.value,
        filter: [],
        maxPages: 1,
        loading: true,
        entries: [],
        count: [],
      },
      this.runSearchQuery,
    );
  };

  runSearchQuery = async () => {
    const { query, page, filter, count } = this.state;

    if (!query) {
      return;
    }

    try {
      this.cancelToken();

      let filterQuery = '';
      if (filter.length > 0) {
        filterQuery = `&subtype=${filter}`;
      }

      const searchResponse = await axios.get(
        `/wp-json/deepsea/v1/search-ventures?page=${page}&per_page=10&s=${query}${filterQuery}`,
        {
          cancelToken: new CancelToken((c) => {
            this.cancelToken = c;
          }),
        },
      );

      if (!Array.isArray(searchResponse.data.posts) || !searchResponse.data.posts.length) {
        this.setState({
          entries: false,
          maxPages: 0,
          loading: false,
          filterLoading: false,
          paginating: false,
        });
        return;
      }

      const types = searchResponse.data.post_types;
      delete types.attachment;
      delete types.wp_block;
      delete types.nav_menu_item;
      delete types.wp_navigation;
      delete types.wp_template;
      delete types.wp_template_part;
      delete types.wp_font_face;
      delete types.wp_font_family;
      delete types.wp_global_styles;

      this.setState({
        entries: searchResponse.data.posts,
        maxPages: searchResponse.data.total_pages,
        loading: false,
        filterLoading: false,
        paginating: false,
        types,
        count: filterQuery === '' ? searchResponse.data.post_types_count : count,
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        this.setState({
          loading: false,
          filterLoading: false,
          paginating: false,
        });
      }
    }
  };

  setFilter = (newFilter) => (event) => {
    const { filter } = this.state;

    if (event.target.checked) {
      filter.push(newFilter);
    } else {
      const index = filter.indexOf(newFilter);
      if (index > -1) {
        filter.splice(index, 1);
      }
    }

    this.setState(
      {
        filter,
        filterLoading: true,
        page: 1,
      },
      this.runSearchQuery,
    );
  };

  loadPage = (props) => {
    const { loading, paginating } = this.state;

    if (paginating || loading) {
      return;
    }

    // Scroll to top of search modal
    const searchModalTop = document.getElementById('search-modal-top');
    gsap.to(searchModalTop, { duration: 0.6, scrollTo: { y: 0, offsetY: 0 } });

    this.setState(
      {
        page: parseInt(props.target.innerText, 10),
        paginating: true,
      },
      this.runSearchQuery,
    );
  };

  closeModal = () => {
    document.documentElement.classList.remove('search-modal-active');
    this.inputRef.current.blur();
  };

  openModal = () => {
    document.documentElement.classList.add('search-modal-active');
    setTimeout(() => this.inputRef.current.focus(), 100);
  };

  handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) {
      this.closeModal();
    }
  };

  handleCrossKeydown = ({ keyCode }) => {
    if (keyCode === 13) {
      this.closeModal();
    }
  };

  PageNumbers = () => {
    const { maxPages, page } = this.state;
    const pages = [];
    for (let i = 1; i <= maxPages; i += 1) {
      if (i === page) {
        pages.push(<span className="page-numbers current">{i}</span>);
      } else if (
        (i < page + 3 && i > page) ||
        (i > page - 3 && i < page) ||
        i === 1 ||
        i === maxPages
      ) {
        pages.push(
          <button onClick={this.loadPage} className="page-numbers" tabIndex="0" type="button">
            {i}
          </button>,
        );
      } else if (i === page + 3 || i === page - 3) {
        pages.push(
          <button className="page-numbers" tabIndex="0" type="button" disabled>
            ...
          </button>,
        );
      }
    }

    return <div className="pagination">{pages}</div>;
  };

  render() {
    const {
      filter,
      query,
      entries,
      page,
      paginating,
      maxPages,
      loading,
      filterLoading,
      types,
      count,
    } = this.state;
    const { PageNumbers } = this;

    return (
      <div className="search-modal-wrapper active">
        <div className="search-modal">
          <div className="search-modal-header">
            <div className="search-modal-header-inner">
              <span className="header-logo" />
              <div className="modal-header--form deepsea-form">
                <div className="input-css-search">
                  <label aria-label="Search">
                    <input
                      id="input-focus"
                      ref={this.inputRef}
                      type="text"
                      onInput={this.handleInputChange}
                      value={query}
                      placeholder="What are you looking for?"
                      onKeyDown={this.handleKeyDown}
                    />
                  </label>
                  <i className="search-submit"></i>
                </div>
              </div>
              <div className="primaryNav-search">
                <button
                  type="button"
                  className="primaryNav-search-button"
                  onClick={this.closeModal}
                  onKeyDown={this.handleCrossKeydown}
                >
                  <i className="icon-x" />
                  Close
                </button>
              </div>
            </div>
          </div>
          <div id="search-modal-top" className="search-modal-results">
            <section className="section">
              <div className="section-wrapper">
                {!loading && entries.length !== 0 && query.length !== 0 && (
                  <div className="wp-block-deepsea-columns has-2-columns wp-block-columns">
                    <div className="wp-block-deepsea-column col-desk-4 col-tab-4 col-mob-12">
                      <h4>Filter By Type</h4>
                      <div className="search-modal-results-filters deepsea-form">
                        {types &&
                          Object.keys(types).map((key) => (
                            <label className="label-checkbox" htmlFor={`post-type-${key}`}>
                              <input
                                checked={filter.includes(key)}
                                onClick={this.setFilter(key)}
                                type="checkbox"
                                id={`post-type-${key}`}
                                name="post-type"
                                value={key}
                                disabled={!count[key]}
                              />
                              <span className={!count[key] && 'label-checkbox--disabled'}>
                                <p>
                                  {types[key].label === 'Posts' ? 'Insights' : types[key].label}
                                  {count[key] ? ` (${count[key]})` : ' (0)'}
                                </p>
                              </span>
                            </label>
                          ))}
                      </div>
                    </div>
                    <div className="wp-block-deepsea-column col-desk-8 col-tab-8 col-mob-12">
                      {!loading && entries.length !== 0 && (
                        <h3>{`Search results for '${query}'`}</h3>
                      )}
                      {!filterLoading && !paginating && entries !== false && (
                        <div className="post-list">
                          <ResultList entries={entries} types={types.data} />
                        </div>
                      )}
                      {maxPages >= page &&
                        maxPages > 1 &&
                        !filterLoading &&
                        !paginating &&
                        entries !== false && <div className="grid-footer">{PageNumbers()}</div>}
                      {(filterLoading || paginating) && (
                        <p className="deepsea-loader deepsea-loader--small">
                          <span className="deepsea-spinner">Loading...</span>
                          Please wait
                        </p>
                      )}
                      {!filterLoading && !paginating && entries === false && (
                        <p>No results found</p>
                      )}
                    </div>
                  </div>
                )}
                {loading && !paginating && entries.length === 0 && query.length !== 0 && (
                  <div className="deepsea-loader-wrap">
                    <p className="deepsea-loader">
                      <span className="deepsea-spinner">Loading...</span>
                      Please wait
                    </p>
                  </div>
                )}
                {!loading && !paginating && entries.length === 0 && query.length !== 0 && (
                  <h2>No results found</h2>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

const setup = () => {
  const $ele = document.getElementById('search-module-ventures');
  if ($ele) {
    render(<SearchModule />, $ele);
  }
};
setup();
